import React, { useEffect, useMemo, useState } from 'react';

import type { UserType } from '@shared/auth/index.js';
import type { HealthCategoryId } from '@shared/patient-intake-status';

import { useAuth, useSafePatientPageContext } from '../../../hooks/index.js';
import { USER_TYPES } from '../../../utils/index.js';
import { PatientIntakeStatusContext } from './PatientIntakeStatusContext.tsx';
import { useGetIntakeStatusCustomSteps, usePatientIntakeStatus } from './hooks';

const HEALTHCARE_PROVIDER_USER_TYPES: Array<UserType | ''> = [
  USER_TYPES.DOCTOR,
  USER_TYPES.NURSE,
  USER_TYPES.SUPPORT,
  USER_TYPES.DIRECTOR,
];

const isUserHealthcareProvider = (userType: UserType | '') => HEALTHCARE_PROVIDER_USER_TYPES.includes(userType);

type PatientIntakeStatusProviderProps = {
  children: React.ReactNode;
  patientId: string;
};

export const PatientIntakeStatusProvider = ({ children, patientId }: PatientIntakeStatusProviderProps) => {
  const { userType } = useAuth();
  const { actions: patientPageModalActions } = useSafePatientPageContext();

  const isHealthcareProvider = isUserHealthcareProvider(userType);
  const {
    data: baseSteps,
    healthCategories,
    isLoading: isBaseStepsLoading,
    error: baseStepsError,
  } = usePatientIntakeStatus(patientId);
  const [activeHealthCategoryId, setActiveHealthCategoryId] = useState<HealthCategoryId | null>(null);

  const activeHealthCategoryIds = useMemo(
    () =>
      Object.keys(healthCategories || {}).filter(
        (healthCategoryId) => healthCategories?.[healthCategoryId as HealthCategoryId] === 'AWAITING_REVIEW',
      ) as HealthCategoryId[],
    [healthCategories],
  );

  useEffect(() => {
    if (!activeHealthCategoryId && activeHealthCategoryIds?.length) {
      setActiveHealthCategoryId(activeHealthCategoryIds[0] ?? null);
    }
  }, [activeHealthCategoryIds, activeHealthCategoryId]);

  const {
    data: customStepsIntakeStatus,
    error: customStepsIntakeStatusError,
    isLoading: isCustomStepsIntakeStatusLoading,
  } = useGetIntakeStatusCustomSteps({
    patientId,
    enabled: !isBaseStepsLoading,
    healthCategoryIds: isHealthcareProvider ? undefined : activeHealthCategoryIds,
  });

  const value = useMemo(
    () => ({
      patientId,
      baseSteps,
      healthCategories,
      isHealthcareProvider,
      activeHealthCategoryId,
      activeHealthCategoryIds,
      patientPageModalActions,
      setActiveHealthCategoryId,
      customSteps: customStepsIntakeStatus,
      error: baseStepsError || customStepsIntakeStatusError,
      isLoading: isBaseStepsLoading || isCustomStepsIntakeStatusLoading,
    }),
    [
      baseSteps,
      patientId,
      baseStepsError,
      healthCategories,
      isBaseStepsLoading,
      isHealthcareProvider,
      activeHealthCategoryId,
      activeHealthCategoryIds,
      customStepsIntakeStatus,
      patientPageModalActions,
      setActiveHealthCategoryId,
      customStepsIntakeStatusError,
      isCustomStepsIntakeStatusLoading,
    ],
  );

  return <PatientIntakeStatusContext.Provider value={value}>{children}</PatientIntakeStatusContext.Provider>;
};
